<script>
  import Layout from '/components/Layout.svelte';
  import { inertia, router, page } from '@inertiajs/svelte';

  export let groups = [];
  export let total = 0;
  export let total_pages = 0;
  export let page_num = 1;
  export let per_page = 60;
  export let search = '';

  $: flash = $page.props.flash || {};

  let searchInput = search;
  let searchTimer;
  let pendingSearch = false;

  function onSearchInput() {
    pendingSearch = true;
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      router.get('/images', { search: searchInput, page: 1 }, {
        preserveState: true,
        replace: true,
        onFinish: () => { pendingSearch = false; }
      });
    }, 400);
  }

  // Only sync the prop back into the input when there's no pending navigation
  $: if (!pendingSearch) searchInput = search;

  function goToPage(p) {
    router.get('/images', { search: searchInput, page: p }, { preserveState: false, replace: true });
  }

  $: pages = buildPages(page_num, total_pages);

  function buildPages(current, total) {
    if (total <= 1) return [];
    const delta = 2;
    const range = [];
    for (let i = Math.max(1, current - delta); i <= Math.min(total, current + delta); i++) {
      range.push(i);
    }
    const result = [];
    if (range[0] > 1) {
      result.push(1);
      if (range[0] > 2) result.push('...');
    }
    range.forEach(p => result.push(p));
    if (range[range.length - 1] < total) {
      if (range[range.length - 1] < total - 1) result.push('...');
      result.push(total);
    }
    return result;
  }
</script>

<svelte:head>
  <title>Images — Age of Trevor Webedit</title>
</svelte:head>

<Layout title="Images">
  {#if flash.notice}
    <div class="flash flash-notice">{flash.notice}</div>
  {/if}
  {#if flash.alert}
    <div class="flash flash-alert">{flash.alert}</div>
  {/if}

  <div class="toolbar">
    <input
      class="search-input"
      type="search"
      placeholder="Filter by path, filename, description, or caption…"
      bind:value={searchInput}
      on:input={onSearchInput}
    />
  </div>

  <div class="file-count">
    {total} image group{total === 1 ? '' : 's'}
    {#if total_pages > 1}· page {page_num} of {total_pages}{/if}
  </div>

  {#if groups.length === 0}
    <p class="empty">No images found.</p>
  {:else}
    <div class="grid">
      {#each groups as group}
        {@const latest = group.latest}
        <a use:inertia href="/images/{group.path}" class="card">
          <div class="thumb-wrap">
            {#if latest}
              <img
                class="thumb"
                src="/image-files/{group.path}/{latest.filename}"
                alt={group.display}
                loading="lazy"
              />
            {:else}
              <div class="thumb-missing">no image</div>
            {/if}
          </div>
          <div class="card-body">
            <div class="card-name">{group.display}</div>
            <div class="card-meta">
              {group.versions.length} version{group.versions.length === 1 ? '' : 's'}
              {#if latest}· v{String(latest.number).padStart(3, '0')}_{latest.label}{latest.ext}{/if}
            </div>
          </div>
        </a>
      {/each}
    </div>

    {#if pages.length > 0}
      <div class="pagination">
        <button class="page-btn" disabled={page_num <= 1} on:click={() => goToPage(page_num - 1)}>‹</button>
        {#each pages as p}
          {#if p === '...'}
            <span class="page-ellipsis">…</span>
          {:else}
            <button
              class="page-btn"
              class:active={p === page_num}
              on:click={() => goToPage(p)}
            >{p}</button>
          {/if}
        {/each}
        <button class="page-btn" disabled={page_num >= total_pages} on:click={() => goToPage(page_num + 1)}>›</button>
      </div>
    {/if}
  {/if}
</Layout>

<style>
  .flash {
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    margin-bottom: 1.5rem;
    font-size: 0.9rem;
    font-weight: 500;
  }
  .flash-notice { background: #f0fdf4; border: 1px solid #86efac; color: #166534; }
  .flash-alert  { background: #fef2f2; border: 1px solid #fca5a5; color: #dc2626; }

  .toolbar {
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .search-input {
    flex: 1;
    padding: 0.5rem 0.875rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    font-size: 0.95rem;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .search-input:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59,130,246,0.15);
  }

  .file-count {
    font-size: 0.8rem;
    color: #94a3b8;
    margin-bottom: 1rem;
  }

  .empty {
    color: #94a3b8;
    font-style: italic;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 1rem;
  }

  .card {
    display: flex;
    flex-direction: column;
    border: 1px solid #e2e8f0;
    border-radius: 0.75rem;
    overflow: hidden;
    text-decoration: none;
    color: #1e293b;
    background: #f8fafc;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .card:hover {
    border-color: #93c5fd;
    box-shadow: 0 2px 8px rgba(59,130,246,0.12);
  }

  .thumb-wrap {
    width: 100%;
    aspect-ratio: 1;
    background: #e2e8f0;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }
  .thumb {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 0.5rem;
  }
  .thumb-missing {
    font-size: 0.75rem;
    color: #94a3b8;
    font-style: italic;
  }

  .card-body {
    padding: 0.6rem 0.75rem;
    border-top: 1px solid #e2e8f0;
  }
  .card-name {
    font-size: 0.8rem;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .card-meta {
    font-size: 0.7rem;
    color: #94a3b8;
    margin-top: 0.15rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-family: 'Fira Code', monospace;
  }

  .pagination {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    justify-content: center;
    margin-top: 2rem;
    flex-wrap: wrap;
  }

  .page-btn {
    min-width: 2rem;
    height: 2rem;
    padding: 0 0.5rem;
    border: 1px solid #e2e8f0;
    border-radius: 0.375rem;
    background: #f8fafc;
    color: #374151;
    font-size: 0.875rem;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
  }
  .page-btn:hover:not(:disabled) {
    background: #e0f2fe;
    border-color: #93c5fd;
  }
  .page-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .page-btn.active {
    background: #1d4ed8;
    border-color: #1d4ed8;
    color: white;
    font-weight: 700;
  }

  .page-ellipsis {
    color: #94a3b8;
    font-size: 0.875rem;
    padding: 0 0.25rem;
  }
</style>
